import React, { useEffect, useState } from 'react';

import {
    GetContractDealsByIdsInput,
    GetContractDealsByIdsOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { useHistory } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { RateCardDetailWrapper } from 'src/components/RateCardDetail/RateCardDetailWrapper';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IRateCardDetailParams } from 'src/types/IRateCard';

import { AddPrimeAMFRateCard } from '../AddPrimeAMFRateCard';
import { PageNotFound } from '../Redirects/PageNotFound/PageNotFound';
import { ViewPrimeAMFRateCard } from '../ViewPrimeAMFRateCard';

export const RateCardRouteWrapper = () => {
    const { setTopAlertMsg, metadata } = React.useContext(AppContext);
    const history = useHistory();
    const services = metadata.serviceMapping;

    const { pathname } = useLocation();

    const params: IRateCardDetailParams = useParams();
    const rateCardId = parseInt(params.rateCardId);
    const isEditMode =
        !!rateCardId && (pathname.includes(ROUTES.RATE_CARDS.EDIT) || pathname.includes(ROUTES.RATE_CARDS.ADD));

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const [rateCardServiceId, setRateCardServiceId] = useState<number | undefined>(services[0].serviceId!);
    const [rateCardIsDraft, setRateCardIsDraft] = useState<boolean | undefined>();

    // redirect to the correct edit type based on draft status
    useEffect(() => {
        if (params.editType === 'add' && rateCardIsDraft === false) {
            history.push(`${ROUTES.RATE_CARDS.INDEX}${ROUTES.RATE_CARDS.EDIT}/${params.rateCardId}`);
        } else if (params.editType === 'edit' && rateCardIsDraft === true) {
            history.push(`${ROUTES.RATE_CARDS.INDEX}${ROUTES.RATE_CARDS.ADD}/${params.rateCardId}`);
        }
    }, [rateCardIsDraft, params.editType]);

    useEffect(() => {
        // Use GetContractDealsByIds to fetch the serviceId for the regarding contractId
        contractConfigurationClient
            .getContractDealsByIds(
                GetContractDealsByIdsInput.clone({
                    contractIds: [rateCardId],
                    includeDraftContracts: true,
                    includeDeletedContracts: true,
                    includeDeletedContractDeals: true,
                    includeIneligibleContractDeals: true
                })
            )
            .subscribe(
                (response: GetContractDealsByIdsOutput) => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    setRateCardServiceId(response?.contractDeals?.[0]?.serviceId);
                    setRateCardIsDraft(response?.contractDeals?.[0]?.contractIsDraft);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getContractDealsByIds unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    }, []);

    let componentToRender = (
        <Row width="100%" height="100vh" alignmentHorizontal="center">
            <Column alignmentHorizontal="center" alignmentVertical="center">
                <Loader size="large" />
                <Text type="b200" color="secondary">
                    Redirecting to contract screen...
                </Text>
            </Column>
        </Row>
    );

    if (isEditMode && rateCardIsDraft !== (params.editType === 'add')) {
        // the edit type doesn't match the contract type
        // draft -> /add ; published -> /edit
        // keep showing the loader while we redirect
    } else if (rateCardServiceId === services[1].serviceId) {
        componentToRender = <RateCardDetailWrapper />;
    } else if (rateCardServiceId === services[2].serviceId || rateCardServiceId === services[3].serviceId) {
        componentToRender = !isEditMode ? (
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            <ViewPrimeAMFRateCard serviceId={rateCardServiceId!} rateCardId={rateCardId} />
        ) : (
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            <AddPrimeAMFRateCard serviceId={rateCardServiceId!} />
        );
    } else if (rateCardServiceId === undefined) {
        componentToRender = <PageNotFound />;
    }

    return componentToRender;
};
